/**
 * GDS 风格标签主题配色
 * ------------------------------------------------------------------
 * 4 款配色取自设计稿 demo，统一在此定义，供以下三处共用，避免色值散落：
 *   1. components/modals/TemplateSelector  —— 模板卡片主色
 *   2. pages/LabelCreator                  —— 初始背景色 / 页头样式
 *   3. components/label-content/styles/label-content.css —— 通过 data 属性 + CSS 变量换肤
 *
 * 色值命名与 demo 的 token 保持一致：
 *   brand   主色（标识、强调）
 *   deep    深色（标题文字、悬停态、深色底）
 *   soft    浅色底（标签、浅底块）
 *   line    发丝线 / 描边
 *   paper   页面底色
 *   gradFrom / gradTo  页头渐变起止色
 *   headBg / headFg    表格表头的前景/背景（保证对比度）
 *   ink     正文色
 */

export const GDS_THEMES = {
  'gds-navy': {
    key: 'gds-navy',
    suffix: 'navy',
    name: '基线深蓝',
    label: '基线深蓝模板',
    description: 'GDS 基线配色，深蓝页头 + 白卡片，最贴近设计稿，适合常规食品标签',
    brand: '#163b73',
    deep: '#0d2d4f',
    soft: '#eef1f6',
    line: '#dde4ee',
    paper: '#f5f7f9',
    gradFrom: '#17335c',
    gradTo: '#102542',
    accent: '#2f6b4f',
    headBg: '#163b73',
    headFg: '#ffffff',
    ink: '#17283b',
    ink2: '#3b4a59',
  },
  'gds-green': {
    key: 'gds-green',
    suffix: 'green',
    name: '绿色',
    label: '绿色模板',
    description: '天然 / 健康调性，墨绿页头 + 浅绿底，适合果蔬、谷物、有机类产品',
    brand: '#2f7d5b',
    deep: '#1f5c42',
    soft: '#e7f2ec',
    line: '#c9e2d5',
    paper: '#f4f8f5',
    gradFrom: '#245c46',
    gradTo: '#143528',
    accent: '#2f7d5b',
    headBg: '#2f7d5b',
    headFg: '#ffffff',
    ink: '#17283b',
    ink2: '#3b4a59',
  },
  'gds-yellow': {
    key: 'gds-yellow',
    suffix: 'yellow',
    name: '黄色',
    label: '黄色模板',
    description: '暖金色调，金色页头 + 米白底，适合烘焙、零食、粮油类产品',
    brand: '#f8bc31',
    deep: '#b45309',
    soft: '#fdf3d3',
    line: '#f3e2ac',
    paper: '#fbf7ec',
    gradFrom: '#fdd45f',
    gradTo: '#eda412',
    accent: '#8a5a06',
    // 金黄底配白字对比度不足，表头改用深金底
    headBg: '#b45309',
    headFg: '#ffffff',
    ink: '#17283b',
    ink2: '#5b4a2e',
  },
  'gds-lightblue': {
    key: 'gds-lightblue',
    suffix: 'lightblue',
    name: '亮蓝',
    label: '亮蓝模板',
    description: '清爽亮蓝，科技感更强，适合饮品、乳品、功能食品',
    brand: '#4a90e2',
    deep: '#2c66ad',
    soft: '#e7f0fb',
    line: '#cfe2f8',
    paper: '#f2f6fb',
    gradFrom: '#4a90e2',
    gradTo: '#2c66ad',
    accent: '#35659d',
    headBg: '#2c66ad',
    headFg: '#ffffff',
    ink: '#17283b',
    ink2: '#3b4a59',
  },
};

/** 4 款 GDS 主题的 variant 标识 */
export const GDS_VARIANTS = Object.keys(GDS_THEMES);

/** 判断某个 templateVariant 是否属于 GDS 系列 */
export function isGdsVariant(variant) {
  return GDS_VARIANTS.includes(variant);
}

/** 取主题配置，非 GDS 变体返回 null（调用方需自行兜底） */
export function getGdsTheme(variant) {
  return GDS_THEMES[variant] || null;
}

/** 生成页头默认样式（深色渐变 + 白字） */
export function getGdsHeaderStyle(variant) {
  const theme = getGdsTheme(variant);
  if (!theme) return null;
  return {
    bgMode: 'gradient',
    gradientFrom: theme.gradFrom,
    gradientTo: theme.gradTo,
    textColor: '#ffffff',
  };
}

/** 供 CSS 使用的内联自定义属性（渲染时挂到根节点，保证主题色随数据走） */
export function getGdsCssVars(variant) {
  const theme = getGdsTheme(variant);
  if (!theme) return {};
  return {
    '--gds-brand': theme.brand,
    '--gds-deep': theme.deep,
    '--gds-soft': theme.soft,
    '--gds-line': theme.line,
    '--gds-paper': theme.paper,
    '--gds-head-bg': theme.headBg,
    '--gds-head-fg': theme.headFg,
    '--gds-ink': theme.ink,
    '--gds-ink-2': theme.ink2,
  };
}

export default GDS_THEMES;
